// Type-only: @ac-expo/api-types is a devDependency here, nothing of it may reach the runtime.
import type { TicketBuyerLimits } from "@ac-expo/api-types";

// A caller with no session: no group, nothing purchased yet.
const ANONYMOUS_BUYER_LIMITS: TicketBuyerLimits = {
  groupIds: [],
  purchasedCountsByTicketType: {},
};

type GroupLimitLike = { groupId: string };

export type TicketTypeWithGroupLimits<
  L extends GroupLimitLike = GroupLimitLike,
> = {
  hasGroupVisibilityAndBuyingLimits: boolean;
  groupLimits?: L[] | null;
};

export type TicketTypeWithLimits = TicketTypeWithGroupLimits<{
  groupId: string;
  maxTicketsPerUser: number | null;
}> & {
  buyingLimitForUserType: number | null;
};

// The « Groupes d'utilisateurs » toggle gates every group effect: with it off, the attached
// groups neither reveal the ticket nor cap it.
export const getBuyerGroupLimits = <L extends GroupLimitLike>(
  ticketType: TicketTypeWithGroupLimits<L>,
  buyerGroupIds: string[]
): L[] => {
  if (!ticketType.hasGroupVisibilityAndBuyingLimits) return [];

  return (ticketType.groupLimits ?? []).filter((limit) =>
    buyerGroupIds.includes(limit.groupId)
  );
};

// Per-person limit the server enforces: the smallest of the ticket type's own limit and the
// buyer's group maximums, or null when none is set. Shared with the ticketing so the quantity
// selectors stop exactly where the basket validation would refuse.
export const getEffectiveTicketLimit = (
  ticketType: TicketTypeWithLimits,
  buyerGroupIds: string[]
): number | null => {
  const groupLimitValues = getBuyerGroupLimits(ticketType, buyerGroupIds)
    .map((limit) => limit.maxTicketsPerUser)
    .filter((limit): limit is number => limit !== null);

  const groupLimit =
    groupLimitValues.length > 0 ? Math.min(...groupLimitValues) : null;

  const baseLimit = ticketType.buyingLimitForUserType ?? null;

  const effectiveLimits = [baseLimit, groupLimit].filter(
    (limit): limit is number => limit !== null
  );

  return effectiveLimits.length > 0 ? Math.min(...effectiveLimits) : null;
};

export type TicketPurchaseAllowance = {
  // The per-person limit shown to the buyer, null when unlimited.
  limit: number | null;
  // How many more tickets the buyer may take, the ones already purchased deducted.
  remaining: number | null;
};

export const getTicketPurchaseAllowance = (
  ticketType: TicketTypeWithLimits & { id: string },
  buyer: TicketBuyerLimits | undefined = ANONYMOUS_BUYER_LIMITS
): TicketPurchaseAllowance => {
  const limit = getEffectiveTicketLimit(ticketType, buyer.groupIds);

  if (limit === null) return { limit: null, remaining: null };

  const purchasedCount = buyer.purchasedCountsByTicketType[ticketType.id] ?? 0;

  return { limit, remaining: Math.max(0, limit - purchasedCount) };
};
